import React, { FC, useEffect, useState } from 'react';
import { MemberShipLevel } from '../types/membershipLevel';
import { userService } from '../api/userService';
import { useAuth } from '../auth/authContext';
import './membershipManagement.css';

const LIFETIME_SORT_ORDER = 99;

interface MembershipManagementProps {
    onChangeMembership?: (level: MemberShipLevel) => void;
}

const MembershipManagement: FC<MembershipManagementProps> = ({
    onChangeMembership,
}) => {
    const { user } = useAuth();
    const [levels, setLevels] = useState<MemberShipLevel[]>([]);
    const [userLevel, setUserLevel] = useState<MemberShipLevel | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const { getMembershipLevels, getUserMembershipLevel } = userService();
        const fetchLevels = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getMembershipLevels();
                const levelData = await getUserMembershipLevel(user?.id ?? "");
                setLevels(data);
                setUserLevel(levelData);
            } catch {
                setError('Failed to load membership levels. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchLevels();
    }, [user?.id]);

    // Use sortOrder to determine tier rank — higher sortOrder = higher tier = Upgrade.
    const getButtonLabel = (level: MemberShipLevel): 'Current Plan' | 'Upgrade' | 'Downgrade' => {
        if (level.name === userLevel?.name) return 'Current Plan';
        if (userLevel === null) return 'Upgrade';
        return level.sortOrder > userLevel.sortOrder ? 'Upgrade' : 'Downgrade';
    };

    const handleButtonClick = (level: MemberShipLevel) => {
        if (onChangeMembership) {
            onChangeMembership(level);
        } else {
            // TODO: connect to membership change API when endpoint is available
            console.log('Membership change requested:', level.name);
        }
    };

    if (loading) {
        return <p className="membership-status">Loading membership levels...</p>;
    }

    if (error) {
        return <p className="membership-status membership-error">{error}</p>;
    }

    if (levels.length === 0) {
        return <p className="membership-status">No membership levels available.</p>;
    }

    const isLifetime = userLevel?.sortOrder === LIFETIME_SORT_ORDER;

    return (
        <div className="membership-management">
            <h2 className="membership-heading">Membership Plans</h2>

            {isLifetime && userLevel ? (
                <>
                    {/* Full-width lifetime hero card */}
                    <div className="membership-hero-lifetime">
                        <div className="lifetime-hero-glow" />
                        <div className="lifetime-hero-star">&#9733;</div>
                        <h2 className="lifetime-hero-title">{userLevel.displayName}</h2>
                        <p className="lifetime-hero-sub">
                            You have unlocked the highest tier. Enjoy unlimited access, forever.
                        </p>
                        <div className="lifetime-hero-stats">
                            <div className="lifetime-stat">
                                <span className="lifetime-stat__value">Unlimited</span>
                                <span className="lifetime-stat__label">Analyses</span>
                            </div>
                            <div className="lifetime-stat-divider" />
                            <div className="lifetime-stat">
                                <span className="lifetime-stat__value">Unlimited</span>
                                <span className="lifetime-stat__label">Rollover</span>
                            </div>
                            <div className="lifetime-stat-divider" />
                            <div className="lifetime-stat">
                                <span className="lifetime-stat__value">{userLevel.aiModelTier}</span>
                                <span className="lifetime-stat__label">AI Model</span>
                            </div>
                            <div className="lifetime-stat-divider" />
                            <div className="lifetime-stat">
                                <span className="lifetime-stat__value">Free</span>
                                <span className="lifetime-stat__label">Forever</span>
                            </div>
                        </div>
                    </div>

                    {/* Plans the user has surpassed */}
                    {/* {surpassedLevels.length > 0 && (
                        <div className="membership-surpassed-section">
                            <p className="membership-surpassed-label">Plans you&apos;ve surpassed</p>
                            <div className="membership-grid membership-grid--dimmed">
                                {surpassedLevels.map((level) => (
                                    <div key={level.membershipLevelId} className="membership-card membership-card--surpassed">
                                        <h3 className="membership-card__name">{level.displayName}</h3>
                                        <p className="membership-card__price">
                                            {level.price === 0 ? 'Free' : `$${level.price.toFixed(2)}/mo`}
                                        </p>
                                        <ul className="membership-card__features">
                                            <li>
                                                <span className="feature-label">Monthly Analyses</span>
                                                <span>{level.monthlyAnalysisLimit}</span>
                                            </li>
                                            <li>
                                                <span className="feature-label">Rollover Credits</span>
                                                <span>{level.allowRollover ? `Up to ${level.maxRolloverCount}` : 'None'}</span>
                                            </li>
                                            <li>
                                                <span className="feature-label">AI Model Tier</span>
                                                <span>{level.aiModelTier}</span>
                                            </li>
                                        </ul>
                                        <button className="membership-btn membership-btn--included" disabled>
                                            Included
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )} */}
                </>
            ) : (
                <div className="membership-grid">
                    {levels.map((level) => {
                        const isCurrent = level.name === userLevel?.name;
                        const buttonLabel = getButtonLabel(level);

                        return (
                            <div
                                key={level.membershipLevelId}
                                className={`membership-card${isCurrent ? ' membership-card--current' : ''}`}
                            >
                                {isCurrent && <span className="membership-badge">Your Plan</span>}

                                <h3 className="membership-card__name">{level.displayName}</h3>

                                <p className="membership-card__price">
                                    {level.price === 0 ? 'Free' : `$${level.price.toFixed(2)}/mo`}
                                </p>

                                <ul className="membership-card__features">
                                    <li>
                                        <span className="feature-label">Monthly Analyses</span>
                                        <span>{level.monthlyAnalysisLimit}</span>
                                    </li>
                                    <li>
                                        <span className="feature-label">Rollover Credits</span>
                                        <span>
                                            {level.allowRollover ? `Up to ${level.maxRolloverCount}` : 'None'}
                                        </span>
                                    </li>
                                    <li>
                                        <span className="feature-label">AI Model Tier</span>
                                        <span>{level.aiModelTier}</span>
                                    </li>
                                </ul>

                                <button
                                    className={[
                                        'membership-btn',
                                        isCurrent
                                            ? 'membership-btn--current'
                                            : buttonLabel === 'Upgrade'
                                            ? 'membership-btn--upgrade'
                                            : 'membership-btn--downgrade',
                                    ].join(' ')}
                                    disabled={isCurrent}
                                    onClick={() => !isCurrent && handleButtonClick(level)}
                                >
                                    {buttonLabel}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MembershipManagement;
